import { useLocation } from '@tanstack/react-router'
import { Check, Copy, RefreshCw, Sparkles, X } from 'lucide-react'
import React, { useState } from 'react'
import ReactDOM from 'react-dom'
import ReactMarkdown from 'react-markdown'
import { useAnalysis } from '../../hooks/useAnalysis'
import { analyzeExpandedHistory } from '../../lib/analysis-utils'
import { generateAiInsights } from '../../services/ai'
import { useMarketStore } from '../../services/store'
import { useIntradayHistory } from '../../hooks/useDataBus'
import { useSettingsStore } from '../../stores/settingsStore'

interface AiInsightsModalProps {
    isOpen: boolean
    onClose: () => void
    response?: string | null
    isLoading?: boolean
}

export const AiInsightsModal: React.FC<AiInsightsModalProps> = ({
    isOpen,
    onClose,
    response,
    isLoading: externalLoading,
}) => {
    const symbol = useSettingsStore((state) => state.symbol)
    const mode = useSettingsStore((state) => state.mode)
    const range = useSettingsStore((state) => state.range)
    
    const marketData = useMarketStore((s) => s.fullData[symbol])
    const historyData = useIntradayHistory(symbol)
    const analysis = useAnalysis()
    const location = useLocation()
    const [loading, setLoading] = useState(false)
    const [insight, setInsight] = useState<string | null>(null)
    const [copied, setCopied] = useState(false)

    const fetchInsights = React.useCallback(async () => {
        if (!marketData) return // Guard

        try {
            setLoading(true)
            setInsight(null)

            let activePage = 'Home'
            // Check location.pathname safely
            const path = location.pathname
            if (path === '/' || path === '/home') activePage = 'Home'
            else if (path.includes('dashboard')) activePage = 'Dashboard'
            else if (path.includes('scanner')) activePage = 'Scanner'
            else if (path.includes('crossover')) activePage = 'Crossover'
            else if (path.includes('variance')) activePage = 'Variance'
            else if (path.includes('inspector')) activePage = 'Inspector'

            // 1. Get History Summary if available
            const historySummary = historyData
                ? analyzeExpandedHistory(
                      historyData,
                      symbol,
                      Number(range),
                      mode,
                      marketData?.spotPrice || 0,
                  )
                : { trend5m: 'Unknown', shiftTrend: 'Unknown', varianceTrend: 'Unknown', divergence: 'Unknown' }

            // Construct Payload for Standalone fetch
            const payload = {
                symbol: symbol,
                mode: mode.toString(),
                range: Number(range),
                activePage,
                context: {
                    market: {
                        spotPrice: marketData?.spotPrice,
                        vix: marketData?.vix || 0,
                    },
                    analysis: {
                        shift: analysis?.shift || 0,
                        resistanceStrike: analysis?.resistanceStrike || 0,
                        supportStrike: analysis?.supportStrike || 0,
                        variance: analysis?.variance?.finalVariance || 0,
                        pcr: analysis?.pcr || 0,
                    },
                    historySummary,
                },
            }

            const data = await generateAiInsights({ data: payload })

            if (data.success) {
                setInsight(data.ideas)
            } else {
                setInsight(`**Error:** ${data.error || 'Failed to generate insights.'}`)
            }
        } catch (_e) {
            setInsight(`**Error:** Network failure during AI request.`)
        } finally {
            setLoading(false)
        }
    }, [symbol, mode, range, location.pathname, marketData])

    React.useEffect(() => {
        if (response !== undefined) setInsight(response)
        if (externalLoading !== undefined) setLoading(externalLoading)
    }, [response, externalLoading])

    React.useEffect(() => {
        if (isOpen && !insight && !loading) {
            fetchInsights()
        }
    }, [isOpen, fetchInsights, insight, loading])

    const handleCopy = () => {
        if (insight) {
            navigator.clipboard.writeText(insight)
            setCopied(true)
            setTimeout(() => setCopied(false), 2000)
        }
    }

    // Derived Page Context for Display (Simple version)
    const pageContext = 'AI Smart'

    if (!isOpen) return null
    if (typeof document === 'undefined') return null

    return ReactDOM.createPortal(
        <div className="fixed inset-0 z-100 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="relative w-full max-w-3xl bg-white rounded-2xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200 border border-slate-200 flex flex-col max-h-[85vh]">
                {/* Header */}
                <div className="px-6 py-4 bg-linear-to-r from-indigo-600 to-violet-600 flex items-center justify-between shrink-0">
                    <div className="flex items-center gap-3 text-white">
                        <div className="p-2 bg-white/20 rounded-lg backdrop-blur-md">
                            <Sparkles size={20} className="text-amber-300" fill="currentColor" />
                        </div>
                        <div>
                            <h2 className="text-lg font-black tracking-tight">AI Strategies</h2>
                            <p className="text-[10px] uppercase tracking-widest opacity-80 font-bold">
                                {symbol} • {pageContext} Analysis
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-white/20 rounded-full text-white/80 hover:text-white transition-colors"
                    >
                        <X size={20} strokeWidth={3} />
                    </button>
                </div>

                <div className="flex-1 p-6 overflow-y-auto custom-scrollbar bg-slate-50/50">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center h-full min-h-[300px] gap-8 opacity-80">
                            <div className="relative">
                                <div className="w-20 h-20 border-4 border-slate-200 border-t-indigo-600 rounded-full animate-spin"></div>
                                <Sparkles
                                    size={24}
                                    className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-indigo-600 animate-pulse"
                                />
                            </div>
                            <div className="flex flex-col items-center gap-2">
                                <p className="text-sm font-black text-slate-800 uppercase tracking-widest animate-pulse">
                                    Analyzing Market Context...
                                </p>
                            </div>
                        </div>
                    ) : (
                        <div className="prose prose-sm max-w-none prose-headings:font-black prose-headings:text-slate-800 prose-p:text-slate-600 prose-strong:text-indigo-900 prose-li:text-slate-600">
                            <ReactMarkdown>{insight || ''}</ReactMarkdown>
                        </div>
                    )}
                </div>

                <div className="px-6 py-4 bg-white border-t border-slate-100 flex items-center justify-between">
                    <div className="flex gap-2">
                        <button
                            onClick={fetchInsights}
                            disabled={loading}
                            className="flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-lg text-xs font-bold uppercase tracking-wider transition-colors disabled:opacity-50"
                        >
                            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Regen
                        </button>
                    </div>
                    <button
                        onClick={handleCopy}
                        disabled={!insight}
                        className={`flex items-center gap-2 px-6 py-2 rounded-lg text-xs font-bold uppercase tracking-wider transition-all shadow-sm ${copied ? 'bg-emerald-500 text-white' : 'bg-indigo-600 text-white'}`}
                    >
                        {copied ? <Check size={14} /> : <Copy size={14} />} {copied ? 'Copied' : 'Copy'}
                    </button>
                </div>
            </div>
        </div>,
        document.body,
    )
}
